import React, { useState, useEffect, useMemo } from "react";
import { COLORS } from "../../theme/colors";
import { agentApi } from "../../api/client";
import { Modal, Input, Select, TextArea, Button, Label } from "../../components/ui";

const CHANNELS = ["whatsapp", "sms", "voice", "chat"];
const ACTION_TYPES = ["send_message", "start_call", "agent_chat"];
const AUTH_MODES = [
  { value: "hmac", label: "HMAC Signature" },
  { value: "api_key", label: "API Key" },
  { value: "none", label: "None" },
];

// A payload-mapping field is a JSON path into the incoming event payload —
// e.g. `data.customer.phone`. People often confuse this with the literal
// value they want, typing the actual phone number or a sentence. When that
// happens, _resolve_field on the server returns nothing and the webhook
// silently falls back to static_recipients + message_template, masking the
// misconfiguration. This regex catches the common shapes ("+1234567890",
// English sentences, anything with spaces or @) so we can warn the user
// before they save.
const LOOKS_LIKE_VALUE = /^(\+?\d{4,}|.{0,80}\s.{0,80}|.+@.+)$/;
const LOOKS_LIKE_PATH = /^[A-Za-z_$][\w$]*(?:\.[A-Za-z_$][\w$]*|\[\d+\])*$/;

// Exported for unit testing
export function mappingFieldHint(value) {
  const v = (value || "").trim();
  if (!v) return null;
  if (LOOKS_LIKE_PATH.test(v)) return null;  // a clean dot-path — OK
  if (LOOKS_LIKE_VALUE.test(v)) {
    return "This looks like a literal value, not a path into the payload. Expected something like `data.customer.phone` — the JSON key path where the value will live on each incoming event.";
  }
  return "This doesn't look like a valid JSON path (use dot notation, e.g. `data.message`).";
}

// Exported for unit testing — bucket agents live/paused/draft, each
// bucket alpha-sorted by name; live first so the common target is on top.
export function sortAgents(agents) {
  const live = [], paused = [], draft = [];
  for (const a of agents || []) {
    const bucket = a.status === "live" ? live : a.status === "paused" ? paused : draft;
    bucket.push(a);
  }
  const byName = (a, b) => (a.name || "").localeCompare(b.name || "");
  return [...live.sort(byName), ...paused.sort(byName), ...draft.sort(byName)];
}

// Exported for unit testing — Save is blocked when the form is
// incomplete or invalid. agent_chat without an agent is the exact
// silent-failure case we make impossible to save.
export function computeWebhookSaveDisabled(form) {
  const agentRequired = form.action_type === "agent_chat";
  const missingAgent = agentRequired && !form.agent_id;
  return !form.name.trim() || missingAgent;
}

// Exported for unit testing — assemble the create/update request body
// from form state. Empty optional fields collapse to undefined; agent_id
// is only sent for agent_chat; payload_mapping carries only set paths.
export function buildWebhookPayload(form) {
  const payload = {
    name: form.name.trim(),
    description: form.description.trim() || undefined,
    channel: form.channel,
    action_type: form.action_type,
    agent_id: form.action_type === "agent_chat" ? form.agent_id || undefined : undefined,
    auth_mode: form.auth_mode,
    payload_mapping: {},
    static_recipients: form.static_recipients.trim()
      ? form.static_recipients.split(",").map(s => s.trim()).filter(Boolean)
      : undefined,
    message_template: form.message_template.trim() || undefined,
  };
  if (form.recipient_field.trim()) payload.payload_mapping.recipient_field = form.recipient_field.trim();
  if (form.message_field.trim()) payload.payload_mapping.message_field = form.message_field.trim();
  return payload;
}

export default function WebhookFormModal({ webhook, onSave, onClose }) {
  const isEdit = !!webhook;
  const [form, setForm] = useState({
    name: "", description: "", channel: "whatsapp", action_type: "send_message",
    agent_id: "", auth_mode: "hmac",
    recipient_field: "", message_field: "",
    static_recipients: "", message_template: "",
  });
  const [agents, setAgents] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    agentApi.list().then(res => {
      if (res.success) setAgents(res.data || []);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (webhook) {
      const mapping = webhook.payload_mapping || {};
      setForm({
        name: webhook.name || "",
        description: webhook.description || "",
        channel: webhook.channel || "whatsapp",
        action_type: webhook.action_type || "send_message",
        agent_id: webhook.agent_id || "",
        auth_mode: webhook.auth_mode || "hmac",
        recipient_field: mapping.recipient_field || "",
        message_field: mapping.message_field || "",
        static_recipients: (webhook.static_recipients || []).join(", "),
        message_template: webhook.message_template || "",
      });
    }
  }, [webhook]);

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  // Sort agents alphabetically; group live agents at the top because the
  // typical webhook target is a live agent and tenants can have 150+ rows
  // (mirrorsme-ai staging has 177 today). Without sorting the dropdown is
  // unscannable.
  const sortedAgents = useMemo(() => sortAgents(agents), [agents]);

  const recipientHint = mappingFieldHint(form.recipient_field);
  const messageHint = mappingFieldHint(form.message_field);

  // Save is blocked when the form is incomplete or invalid. The
  // agent-chat-without-agent case is the exact one that produced the
  // staging "No agent_id configured for agent_chat" silent failure — make
  // it impossible to save in that state going forward.
  const agentRequired = form.action_type === "agent_chat";
  const missingAgent = agentRequired && !form.agent_id;
  const saveDisabled = computeWebhookSaveDisabled(form);

  const handleSubmit = async () => {
    if (saveDisabled) return;
    setSaving(true);
    await onSave(buildWebhookPayload(form));
    setSaving(false);
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={isEdit ? "Edit Webhook" : "Create Webhook"}
      width={520}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit} loading={saving} disabled={saveDisabled}>
            {isEdit ? "Update" : "Create"}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <Input label="Name *" value={form.name} onChange={e => set("name", e.target.value)}
          placeholder="e.g. Stripe Payment Webhook" />

        <TextArea label="Description" value={form.description} onChange={e => set("description", e.target.value)}
          placeholder="Optional description..." rows={2} />

        {/* Channel + Action Type row */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <Select label="Channel *" value={form.channel} onChange={e => set("channel", e.target.value)}>
            {CHANNELS.map(ch => <option key={ch} value={ch}>{ch}</option>)}
          </Select>
          <Select label="Action Type" value={form.action_type} onChange={e => set("action_type", e.target.value)}>
            {ACTION_TYPES
              .filter(at => at !== "start_call" || form.channel === "voice")
              .map(at => <option key={at} value={at}>{at.replace(/_/g, " ")}</option>)}
          </Select>
        </div>

        {/* Agent picker — REQUIRED when action_type is agent_chat. Without
            this validation, users save webhooks with agent_id=null and the
            background dispatcher later returns
              {"error": "No agent_id configured for agent_chat"}
            on every inbound event — visible only in the event log. */}
        {form.action_type === "agent_chat" && (
          <div>
            <Select label="Agent *" value={form.agent_id} onChange={e => set("agent_id", e.target.value)}>
              <option value="">Select an agent…</option>
              {sortedAgents.map(a => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.status !== "live" ? ` (${a.status})` : ""} — {a.model || "no model"}
                </option>
              ))}
            </Select>
            {missingAgent && (
              <div style={{ fontSize: 10, color: COLORS.red || "#ef4444", marginTop: 4 }}>
                Pick an agent — inbound events go nowhere without one.
              </div>
            )}
            {!missingAgent && (
              <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 4 }}>
                The selected agent will receive every inbound event as a chat message.
                Live agents are listed first; paused/draft agents are still pickable.
              </div>
            )}
          </div>
        )}

        {/* Auth Mode */}
        <div>
          <Label>Authentication</Label>
          <div style={{ display: "flex", gap: 6 }}>
            {AUTH_MODES.map(mode => (
              <button key={mode.value} onClick={() => set("auth_mode", mode.value)} style={{
                flex: 1, padding: "7px 0", borderRadius: 7, fontSize: 11, fontWeight: 600,
                border: `1px solid ${form.auth_mode === mode.value ? COLORS.accent + "40" : COLORS.border}`,
                background: form.auth_mode === mode.value ? COLORS.accentSoft : "transparent",
                color: form.auth_mode === mode.value ? COLORS.accent : COLORS.textMuted,
                cursor: "pointer", transition: "all 0.12s",
              }}>{mode.label}</button>
            ))}
          </div>
        </div>

        {/* Payload Mapping — the second silent-failure trap. The fields
            below hold JSON PATHS into the inbound payload, not literal
            values. Adding a per-field hint (mappingFieldHint) that detects
            common confusion shapes (a phone literal like "+9234…", a
            sentence with spaces, an email address) and warns inline. The
            backend `_resolve_field` silently returns nothing when the path
            doesn't match, so we surface the issue at the form layer where
            it's actionable. */}
        <div style={{ padding: "12px 14px", borderRadius: 10, background: COLORS.surface, border: `1px solid ${COLORS.border}` }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>
            Payload Mapping
          </div>
          <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 10, lineHeight: 1.4 }}>
            Tell the webhook <strong>where to look</strong> on each incoming event for the recipient
            and the message. These are JSON paths — for the payload
            <code style={{ margin: "0 4px", padding: "1px 5px", borderRadius: 4, background: COLORS.bg, fontSize: 10 }}>{'{"data":{"customer":{"phone":"+1…"}}}'}</code>
            the path is <code style={{ padding: "1px 5px", borderRadius: 4, background: COLORS.bg, fontSize: 10 }}>data.customer.phone</code>.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div>
              <Input label="Recipient (path)" value={form.recipient_field}
                onChange={e => set("recipient_field", e.target.value)}
                placeholder="data.customer.phone" style={{ fontSize: 11 }} />
              {recipientHint && (
                <div style={{ fontSize: 10, color: COLORS.orange || "#F59E0B", marginTop: 4, lineHeight: 1.4 }}>
                  {recipientHint}
                </div>
              )}
            </div>
            <div>
              <Input label="Message (path)" value={form.message_field}
                onChange={e => set("message_field", e.target.value)}
                placeholder="data.message" style={{ fontSize: 11 }} />
              {messageHint && (
                <div style={{ fontSize: 10, color: COLORS.orange || "#F59E0B", marginTop: 4, lineHeight: 1.4 }}>
                  {messageHint}
                </div>
              )}
            </div>
          </div>
        </div>

        <Input label="Static Recipients (fallback)" value={form.static_recipients}
          onChange={e => set("static_recipients", e.target.value)}
          placeholder="+1234567890, +0987654321"
          helper="Comma-separated. Used only when the path above resolves to nothing on an event." />

        <TextArea label="Message Template (fallback)" value={form.message_template}
          onChange={e => set("message_template", e.target.value)}
          placeholder={'Hello {{data.customer.name}}, your order {{data.order_id}} is ready.'}
          rows={3} style={{ fontFamily: "monospace" }}
          helper={"Use {{path.to.field}} for dynamic values. The template runs ONLY when no Message path is set, or the path resolves to nothing on an event."} />
      </div>
    </Modal>
  );
}
